import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar, Plus, Pencil, Trash2, Check, X } from 'lucide-react';

function SeasonSelector({
  seasons,
  selectedSeasonId,
  onSelectSeason,
  onAddSeason,
  onRenameSeason,
  onDeleteSeason
}) {
  const [isAdding, setIsAdding] = useState(false);
  const [newSeasonName, setNewSeasonName] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editSeasonName, setEditSeasonName] = useState('');

  const currentSeason = seasons.find(s => s.id === selectedSeasonId);

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (newSeasonName.trim()) {
      await onAddSeason(newSeasonName.trim());
      setNewSeasonName('');
      setIsAdding(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (editSeasonName.trim() && selectedSeasonId) {
      await onRenameSeason(selectedSeasonId, editSeasonName.trim());
      setIsEditing(false);
    }
  };

  const startEditing = () => {
    if (currentSeason) {
      setEditSeasonName(currentSeason.name);
      setIsEditing(true);
    }
  };

  const handleDelete = () => {
    if (!currentSeason) return;
    if (window.confirm(`Are you sure you want to delete season "${currentSeason.name}"? All associated players and games in this season will be deleted.`)) {
      onDeleteSeason(currentSeason.id);
    }
  };

  return (
    <Card className="mb-6 p-4 border bg-card text-card-foreground shadow-sm">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        {/* Left: Season selector & controls */}
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Calendar className="w-4 h-4 text-primary" />
            <span>Season:</span>
          </div>

          <select
            value={selectedSeasonId || 'ALL'}
            onChange={(e) => onSelectSeason(e.target.value === 'ALL' ? null : e.target.value)}
            className="flex h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring font-medium"
          >
            <option value="ALL">All Seasons (Combined View)</option>
            {seasons.map(s => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>

          {/* Quick action buttons for selected season */}
          {selectedSeasonId && currentSeason && !isEditing && (
            <div className="flex items-center gap-1.5">
              <Button
                variant="outline"
                size="sm"
                onClick={startEditing}
                title="Rename active season"
                className="h-8 px-2 text-xs flex items-center gap-1"
              >
                <Pencil className="w-3 h-3" />
                Rename
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleDelete}
                title="Delete active season"
                className="h-8 w-8 hover:bg-destructive/10 border-input"
              >
                <Trash2 className="w-4 h-4 text-red-500 hover:text-red-600" />
              </Button>
            </div>
          )}
        </div>

        {/* Right: Add new season button */}
        {!isAdding && !isEditing && (
          <Button
            size="sm"
            onClick={() => setIsAdding(true)}
            className="flex items-center gap-1.5 self-stretch sm:self-auto justify-center"
          >
            <Plus className="w-4 h-4" />
            Add Season
          </Button>
        )}
      </div>

      {/* Inline Form: Add New Season */}
      {isAdding && (
        <form onSubmit={handleAddSubmit} className="mt-4 pt-3 border-t flex flex-col sm:flex-row gap-2 items-center">
          <Input
            type="text"
            value={newSeasonName}
            onChange={(e) => setNewSeasonName(e.target.value)}
            placeholder="e.g. Spring 2026, Fall 2026"
            className="flex-1 text-sm w-full"
            autoFocus
          />
          <div className="flex gap-2 w-full sm:w-auto justify-end">
            <Button type="submit" size="sm" className="flex items-center gap-1">
              <Check className="w-4 h-4" />
              Create
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => { setIsAdding(false); setNewSeasonName(''); }}
              className="flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Inline Form: Edit Active Season */}
      {isEditing && (
        <form onSubmit={handleEditSubmit} className="mt-4 pt-3 border-t flex flex-col sm:flex-row gap-2 items-center">
          <Input
            type="text"
            value={editSeasonName}
            onChange={(e) => setEditSeasonName(e.target.value)}
            placeholder="Season Name"
            className="flex-1 text-sm w-full"
            autoFocus
          />
          <div className="flex gap-2 w-full sm:w-auto justify-end">
            <Button type="submit" variant="success" size="sm" className="flex items-center gap-1">
              <Check className="w-4 h-4" />
              Save Name
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(false)}
              className="flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Cancel
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

export default SeasonSelector;
